
import { defineComponent } from 'vue';
import { GoogleMapAddressService } from '@/shared/services/GoogleMapAddressService';
import { GoogleMapLoaderService } from '@/shared/services/GoogleMapLoaderService';
import GoogleAutocomplete from './GoogleAutocomplete.vue';
import { mapSettings, customMapSettings } from '@/shared/constants/mapSettings';
import { mapCountries } from '@/shared/constants/mapCountries';
import { Address } from '@/shared/models/Address';
import { Point } from '@/shared/models/Point';
import MapControls from '@/shared/components/MapControls.vue';

export default defineComponent({
  name: 'MapPosition',
  emits: ['addressChanged'],
  components: {
    MapControls,
    GoogleAutocomplete,
  },
  props: {
    position: Object,
    label: {
      type: String,
    },
    country: {
      type: String,
      default: 'sk',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      autocompleteText: null as null | string,
      map: null as null | google.maps.Map,
      marker: null as null | google.maps.Marker,
      geocoder: null as null | google.maps.Geocoder,
    };
  },
  async mounted() {
    await GoogleMapLoaderService.load();
    this.initializeMap();
  },
  computed: {
    mapConfig() {
      const options = {
        ...mapSettings,
      } as google.maps.MapOptions;
      if (this.country) {
        const country = mapCountries[this.country as string];
        options.center = { lat: country.center_lat , lng: country.center_lng };
        options.zoom = country.zoom;
      }
      return options;
    },
  },
  watch: {
    position: function (newVal: null | Point) {
      if (newVal) {
        this.placeMarker(newVal);
      }
    },
  },
  methods: {
    initializeMap() {
      if(!window.google || !window.google.maps) {
        return;
      }
      const mapContainer = this.$refs.googleMap as HTMLElement;
      this.map = new window.google.maps.Map(mapContainer, this.mapConfig);
      this.geocoder = new window.google.maps.Geocoder();
      this.marker = new window.google.maps.Marker({
        map: this.map,
        draggable: true,
        icon: {
          url: customMapSettings.markerIconUrl,
          scaledSize: new window.google.maps.Size(customMapSettings.markerIconSize, customMapSettings.markerIconSize),
        },
      });
      this.marker.addListener('dragend', (e: any) => {
        this.markerChanged(e.latLng);
      });
      this.map.addListener('click', (e: any) => {
        this.placeMarker(e.latLng);
      });
    },
    placeMarker(position: any) {
      if (this.marker) {
        this.marker.setPosition(position);
        this.markerChanged(position);
      }
    },
    markerChanged(position: any) {
      if (this.geocoder) {
        this.geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK') {
            if (results.length > 0) {
              let place: google.maps.GeocoderResult | null = null;
              let address: Address | null = null;

              for (const result of results) {
                place = result;
                address = GoogleMapAddressService.getAddress(place);

                if (address.street) {
                  break;
                }
              }

              if (!address?.street) {
                place = results[0];
                address = GoogleMapAddressService.getAddress(place);
              }

              if (place) {
                this.setMapView(place, true);
              }
              if (address) {
                this.autocompleteText = address.address;
                this.$emit('addressChanged', address);
              }
            } else {
              console.error('No results found');
            }
          } else {
            console.error('Geocoder failed due to: ' + status);
          }
        });
      }
    },
    setMapView(place: google.maps.places.PlaceResult | google.maps.GeocoderResult, markerCenter?: boolean) {
      if (!this.map) return;

      if (place && place.geometry) {
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(12);
        }
      }

      if (markerCenter && this.marker) {
        this.map.panTo((this.marker as any).getPosition());
      }
    },
    onPlaceChanged(address: Address, place: google.maps.places.PlaceResult) {
      this.setMapView(place);
      if (address && this.marker) {
        this.marker.setPosition({ lat: address.latitude, lng: address.longitude });
      }
      this.$emit('addressChanged', address);
    },
  },
});
